/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/Sections/Description/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../../utils/logger.js";
import React, { useState, useEffect, useMemo, useRef } from "react";
import styles from "./Description.module.css";
import { useTranslation } from "react-i18next";
import { ReactComponent as PlusIcon } from "../../../assets/icons/PlusIcon.svg";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/Calendar_Today.svg";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import locale from "antd/es/date-picker/locale/es_ES";
import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { useFieldValidation } from "../../../hooks/useFieldValidation";
import { validateField } from "../../../utils/validation";
import {
  getCompanyFieldValue,
  shouldHideFieldForProfile,
} from "../../../utils/common";

const Description = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const prevHasErrorsRef = useRef(false);
  // Track the previous selectedProfile to detect profile changes
  const prevSelectedProfileRef = useRef(selectedProfile);

  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    description: "",
    keywords: "",
  });
  const [keywords, setKeywords] = useState(() => {
    try {
      const companyKeywords = getCompanyFieldValue(
        company,
        "palabrasClave",
        selectedProfile,
        {},
      );

      const languages = ["es", "en", "fr"];

      languages.forEach((lang) => {
        if (!companyKeywords[lang]) {
          companyKeywords[lang] = [];
        }
      });

      return companyKeywords;
    } catch (error) {
      logger.error("Error parsing keywords:", { err: sanitizeError(error) });
      return {
        es: [],
        en: [],
        fr: [],
      };
    }
  });
  const [descriptions, setDescriptions] = useState(() => {
    try {
      const companyDescriptions = getCompanyFieldValue(
        company,
        "descripcionDeLaEmpresa",
        selectedProfile,
        {},
      );

      const languages = ["es", "en", "fr"];

      languages.forEach((lang) => {
        if (!companyDescriptions[lang]) {
          companyDescriptions[lang] = "";
        }
      });

      return companyDescriptions;
    } catch (error) {
      logger.error("Error parsing descriptions:", {
        err: sanitizeError(error),
      });
      return {
        es: "",
        en: "",
        fr: "",
      };
    }
  });
  const [keywordInput, setKeywordInput] = useState("");
  const [fechaInicioOperaciones, setFechaInicioOperaciones] = useState(
    company?.fechaInicioOperaciones || "",
  );

  const companyKeywords = useMemo(() => {
    const keywords = getCompanyFieldValue(
      company,
      "palabrasClave",
      selectedProfile,
      null,
    );
    if (keywords) {
      return keywords;
    }
    return {
      es: [],
      en: [],
      fr: [],
    };
  }, [company?.palabrasClave, company?.perfiles, selectedProfile]);

  const companyDescriptions = useMemo(() => {
    const descriptions = getCompanyFieldValue(
      company,
      "descripcionDeLaEmpresa",
      selectedProfile,
      null,
    );
    if (descriptions) {
      return descriptions;
    }
    return {
      es: "",
      en: "",
      fr: "",
    };
  }, [company?.descripcionDeLaEmpresa, company?.perfiles, selectedProfile]);

  // Only sync from company data when the selectedProfile changes, not on every update
  // This prevents local changes from being overwritten by stale company data
  useEffect(() => {
    if (prevSelectedProfileRef.current !== selectedProfile) {
      setKeywords(companyKeywords);
      setDescriptions(companyDescriptions);
      prevSelectedProfileRef.current = selectedProfile;
    }
  }, [selectedProfile, companyKeywords, companyDescriptions]);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    dayjs.locale(currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    const currentDescription = descriptions[currentLanguage] || "";
    handleFieldChange("description", currentDescription);
  }, [currentLanguage, descriptions]);

  // Notify parent of validation changes only when the state actually changes
  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handleInputChange = (field, value) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      // Validate the keyword before adding
      handleFieldChange("keywords", keywordInput.trim());

      // Check if validation passes
      const validation = validateField(keywordInput.trim(), "keywords");
      if (validation.isValid) {
        const newKeywords = { ...keywords };
        if (!newKeywords[currentLanguage]) {
          newKeywords[currentLanguage] = [];
        }
        newKeywords[currentLanguage].push(keywordInput.trim());
        setKeywords(newKeywords);
        setKeywordInput("");
        handleInputChange("palabrasClave", newKeywords);
        // Clear validation state after successful addition
        handleFieldChange("keywords", "");
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleDeleteKeyword = (indexToDelete) => {
    const newKeywords = { ...keywords };
    if (newKeywords[currentLanguage]) {
      newKeywords[currentLanguage] = newKeywords[currentLanguage].filter(
        (_, index) => index !== indexToDelete,
      );
      setKeywords(newKeywords);
      handleInputChange("palabrasClave", newKeywords);
    }
  };

  const handleDescriptionChange = (value) => {
    const newDescriptions = { ...descriptions };
    newDescriptions[currentLanguage] = value;
    setDescriptions(newDescriptions);
    handleInputChange("descripcionDeLaEmpresa", newDescriptions);

    handleFieldChange("description", value);
  };

  const getCurrentLanguageKeywords = () => {
    return keywords[currentLanguage] || [];
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = date.format("YYYY-MM-DD");
      setFechaInicioOperaciones(formattedDate);
      handleInputChange("fechaInicioOperaciones", formattedDate);
    }
    setIsCalendarOpen(false);
  };

  const getLocale = () => {
    switch (currentLanguage) {
      case "es":
        return locale;
      case "en":
        return require("antd/es/date-picker/locale/en_US").default;
      case "fr":
        return require("antd/es/date-picker/locale/fr_FR").default;
      default:
        return locale;
    }
  };

  // Alternate layout for profile 2/3 (when fechaInicioOperaciones is hidden)
  const isAlternateLayout = selectedProfile !== 0;

  return (
    <div
      className={`${styles.sectionContent} ${isAlternateLayout ? styles.alternateLayout : ""}`}
    >
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {t("components.description.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>

      {/* Original layout for Profile 1 */}
      {!isAlternateLayout && (
        <>
          <div className={`${styles.inputRow} ${styles.descriptionInputRow}`}>
            {!shouldHideFieldForProfile(
              "fechaInicioOperaciones",
              selectedProfile,
            ) && (
              <div className={`${styles.inputGroup} ${styles.leftInputBox}`}>
                <label
                  className={`${styles.inputLabel} ${styles.leftInputLabel}`}
                >
                  {t("components.description.startDateLabel")}
                </label>
                <div className={styles.inputWithIconContainer}>
                  <DatePicker
                    open={isCalendarOpen}
                    value={
                      fechaInicioOperaciones
                        ? dayjs(fechaInicioOperaciones)
                        : null
                    }
                    onChange={handleDateChange}
                    onOpenChange={setIsCalendarOpen}
                    format="DD/MM/YYYY"
                    inputReadOnly={true}
                    suffixIcon={
                      <CalendarIcon className={styles.calendarIcon} />
                    }
                    locale={getLocale()}
                    disabled={true}
                  />
                </div>
              </div>
            )}
            <div className={`${styles.inputGroup} ${styles.rightInputBox}`}>
              <label className={styles.inputLabel}>
                {t("components.description.keyWordsLabel")}
              </label>
              <div className={styles.inputWithButtonContainer}>
                <input
                  type="text"
                  className={`${styles.input} ${
                    getFieldValidationState("keywords", keywordInput) ===
                    "error"
                      ? styles.inputError
                      : ""
                  }`}
                  placeholder={t("components.description.keyWordsPlaceholder")}
                  value={keywordInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setKeywordInput(value);
                    handleFieldChange("keywords", value);
                  }}
                  onKeyPress={handleKeyPress}
                  maxLength={255}
                />
                <button
                  className={styles.addKeyWordButton}
                  onClick={handleAddKeyword}
                >
                  <PlusIcon className={styles.plusIcon} />
                  <span>{t("components.description.descriptionButton")}</span>
                </button>
              </div>
              {getErrorMessage("keywords") && (
                <p className={styles.errorMessage}>
                  {getErrorMessage("keywords")}
                </p>
              )}
            </div>
          </div>
          <div className={styles.inputRow}>
            <div className={`${styles.inputGroup} ${styles.descriptionGroup}`}>
              <label className={styles.inputLabel}>
                {t("components.description.descriptionLabel")}
              </label>
              <textarea
                className={`${styles.input} ${styles.descriptionInput} ${
                  getFieldValidationState(
                    "description",
                    descriptions[currentLanguage] || "",
                  ) === "error"
                    ? styles.inputError
                    : ""
                }`}
                placeholder={t("components.description.descriptionPlaceholder")}
                value={descriptions[currentLanguage] || ""}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                maxLength={500}
              />
              {getErrorMessage("description") && (
                <p className={styles.errorMessage}>
                  {getErrorMessage("description")}
                </p>
              )}
              <p className={styles.notVisibleFieldMessage}>
                *{t("components.corporateIdentity.mandatoryFieldMessage")}
              </p>
            </div>
            <div className={styles.keywordsDisplayContainer}>
              <div className={styles.keywordsList}>
                {getCurrentLanguageKeywords().map((keyword, index) => (
                  <span key={index} className={styles.keyword}>
                    {keyword}
                    <button
                      className={styles.deleteKeywordButton}
                      onClick={() => handleDeleteKeyword(index)}
                      aria-label={t("components.description.deleteKeywordAria")}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Alternate layout for Profile 2/3: Description on left, Keywords on right */}
      {isAlternateLayout && (
        <div className={styles.alternateRow}>
          <div
            className={`${styles.inputGroup} ${styles.descriptionGroupAlternate}`}
          >
            <label className={styles.inputLabel}>
              {t("components.description.descriptionLabel")}
            </label>
            <textarea
              className={`${styles.input} ${styles.descriptionInput} ${styles.descriptionInputAlternate} ${
                getFieldValidationState(
                  "description",
                  descriptions[currentLanguage] || "",
                ) === "error"
                  ? styles.inputError
                  : ""
              }`}
              placeholder={t("components.description.descriptionPlaceholder")}
              value={descriptions[currentLanguage] || ""}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              maxLength={500}
            />
            {getErrorMessage("description") && (
              <p className={styles.errorMessage}>
                {getErrorMessage("description")}
              </p>
            )}
            <p className={styles.notVisibleFieldMessage}>
              *{t("components.corporateIdentity.mandatoryFieldMessage")}
            </p>
          </div>
          <div className={styles.keywordsColumnAlternate}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>
                {t("components.description.keyWordsLabel")}
              </label>
              <div className={styles.inputWithButtonContainerAlternate}>
                <input
                  type="text"
                  className={`${styles.input} ${styles.keywordsInputAlternate} ${
                    getFieldValidationState("keywords", keywordInput) ===
                    "error"
                      ? styles.inputError
                      : ""
                  }`}
                  placeholder={t("components.description.keyWordsPlaceholder")}
                  value={keywordInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setKeywordInput(value);
                    handleFieldChange("keywords", value);
                  }}
                  onKeyPress={handleKeyPress}
                  maxLength={255}
                />
                <button
                  className={styles.addKeyWordButton}
                  onClick={handleAddKeyword}
                >
                  <PlusIcon className={styles.plusIcon} />
                  <span>{t("components.description.descriptionButton")}</span>
                </button>
              </div>
              {getErrorMessage("keywords") && (
                <p className={styles.errorMessage}>
                  {getErrorMessage("keywords")}
                </p>
              )}
            </div>
            <div className={styles.keywordsDisplayContainerAlternate}>
              <div className={styles.keywordsList}>
                {getCurrentLanguageKeywords().map((keyword, index) => (
                  <span key={index} className={styles.keyword}>
                    {keyword}
                    <button
                      className={styles.deleteKeywordButton}
                      onClick={() => handleDeleteKeyword(index)}
                      aria-label={t("components.description.deleteKeywordAria")}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Description;
